import { theme } from '@abdoo/common';
import { Skeleton } from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { differenceInDays } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type CustomLabel = {
  text: string;
  percentage: number;
};

interface GraphicProgressProps {
  remaining: number;
  entitlement: number;
  isEntitlementHealthy: boolean;
  holidayYearEnd: Date;
  isLoading: boolean;
}

const hexToRGB = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else {
    return `rgb(${r}, ${g}, ${b})`;
  }
};

const getBarPercentageWithInitialProgress = (
  numerator: number,
  denominator: number,
  initialProgress = 2,
) => {
  const percent = Math.round((numerator / denominator) * 100);
  return percent > initialProgress ? percent : initialProgress;
};

export const GraphicProgress = ({
  remaining,
  entitlement,
  isEntitlementHealthy,
  holidayYearEnd,
  isLoading,
}: GraphicProgressProps) => {
  const daysLeftInYear = useMemo(
    () => differenceInDays(holidayYearEnd, new Date()),
    [holidayYearEnd],
  );
  const remainingEntitlementPercent = useMemo(() => {
    return getBarPercentageWithInitialProgress(entitlement - remaining, entitlement);
  }, [entitlement, remaining]);
  const yearLeftPercent = useMemo(() => {
    return getBarPercentageWithInitialProgress(365 - daysLeftInYear, 365);
  }, [daysLeftInYear]);

  const bookingsColor = theme.palette.remaining.main;
  const yearColor = isEntitlementHealthy
    ? theme.palette.success.main
    : theme.palette.error.main;

  const labelFont = useMemo(
    () => `12px "${theme.typography.body1.fontFamily}", Helvetica, Arial, sans-serif`,
    [],
  );

  const customLabels: CustomLabel[] = useMemo(() => {
    return [
      {
        text: `${remaining} of ${entitlement} days remaining`,
        percentage: remainingEntitlementPercent,
      },
      {
        text: `${daysLeftInYear} days to use entitlement`,
        percentage: yearLeftPercent,
      },
    ];
  }, [
    daysLeftInYear,
    entitlement,
    remaining,
    remainingEntitlementPercent,
    yearLeftPercent,
  ]);

  const renderLabel = (
    chart: ChartJS,
    fontName: string,
    barIndex: number,
    { percentage, text }: CustomLabel,
  ) => {
    const index = percentage <= 50;
    const ctx = chart.ctx;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const meta = chart.getDatasetMeta(index ? 2 : 0);
    const bar = meta.data[barIndex] as unknown as BarElement;
    const { x, y } = bar.getCenterPoint();
    ctx.font = index ? fontName : `bold ${fontName}`;
    ctx.fillStyle = index ? 'black' : 'white';
    ctx.fillText(text, x, y);
  };

  const renderLabels = useCallback(
    (e: any) => {
      customLabels.forEach((label, i) => {
        renderLabel(e.chart, labelFont, i, label);
      });
    },
    [customLabels, labelFont],
  );

  const options: React.ComponentProps<typeof Bar>['options'] = useMemo(() => {
    return {
      indexAxis: 'y',
      elements: {
        bar: {
          borderWidth: 2,
        },
      },
      events: [],
      maintainAspectRatio: false,
      responsive: true,
      font: {
        family: theme.typography.body1.fontFamily,
      },
      transitions: {
        resize: {
          animation: {
            duration: 400,
          },
        },
      },
      animation: {
        onProgress(e) {
          renderLabels(e);
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          stacked: true,
          min: 0,
          max: 100,
          ticks: { display: false },
          grid: { display: false, drawOnChartArea: false, drawTicks: false },
        },
        y: {
          grid: { display: false, drawOnChartArea: false, drawTicks: false },
          ticks: { display: false },
          stacked: true,
          labels: ['', ''],
        },
      },
    };
  }, [renderLabels]);

  const data: React.ComponentProps<typeof Bar>['data'] = useMemo(() => {
    const dataSet1 = [remainingEntitlementPercent, yearLeftPercent];
    const dataSet2 = [100 - remainingEntitlementPercent, 100 - yearLeftPercent];

    return {
      datasets: [
        {
          label: 'Remaining entitlement',
          data: dataSet1,
          categoryPercentage: 0.975,
          barPercentage: 1.0,
          borderColor: [bookingsColor, hexToRGB(yearColor, 0.75)],
          borderSkipped: true,
          backgroundColor: [
            hexToRGB(bookingsColor, 1),
            hexToRGB(yearColor, isEntitlementHealthy ? 1 : 0.75),
          ],
        },
        {
          label: '',
          data: [0.75, 0.75], // gap between the bars
          categoryPercentage: 0.975,
          barPercentage: 1.0,
          borderSkipped: true,
          backgroundColor: 'white',
        },
        {
          label: 'Year remaining',
          data: dataSet2,
          categoryPercentage: 0.975,
          barPercentage: 1.0,
          borderSkipped: true,
          backgroundColor: [hexToRGB(bookingsColor, 0.25), hexToRGB(yearColor, 0.25)],
        },
      ],
    };
  }, [
    remainingEntitlementPercent,
    yearLeftPercent,
    bookingsColor,
    yearColor,
    isEntitlementHealthy,
  ]);

  if (isLoading) {
    return <Skeleton variant="rounded" height="100%" width="100%" />;
  }

  return <Bar options={options} data={data} />;
};
